import React from "react";
import { cn } from "../../../lib/utils";
import { formatTimestamp, formatDuration, eventTypeLabel } from "../utils/gatewayUtils";
import GatewayStatusBadge from "./GatewayStatusBadge";
import GatewayModuleBadge from "./GatewayModuleBadge";
import "../AgenticGateway.css";

function EventDetail({ event }: { event: any }) {
  const p = event.responsePayload || {};
  const bits: React.ReactNode[] = [];
  const code = (v: React.ReactNode) => <code className="text-primary font-mono">{v}</code>;
  if (event.eventType === "STS_TOKEN_MINTED") {
    if (p.scope) bits.push(<>Scope {code(p.scope)}</>);
    if (p.ttlSeconds) bits.push(<>TTL {p.ttlSeconds}s</>);
    if (p.aud) bits.push(<>Audience {code(p.aud)}</>);
  }
  if (event.pdpDecision) bits.push(<>Decision <strong className="text-foreground/90">{event.pdpDecision}</strong></>);
  if (event.pdpPolicyId) bits.push(<>Decided by {code(event.pdpPolicyId)}</>);
  if (event.pdpReason) bits.push(<>{event.pdpReason}</>);
  if (event.capabilityName && event.eventType !== "STS_TOKEN_MINTED") bits.push(<>{event.capabilityName}</>);
  if (event.serverName) bits.push(<>Server {event.serverName}</>);
  const dur = event.durationMs ?? event.duration;
  if (dur != null) bits.push(<>{formatDuration(dur)}</>);
  if (!bits.length) return null;
  return (
    <div className="text-[11px] text-muted-foreground mt-1 break-words leading-relaxed">
      {bits.map((b, i) => (
        <React.Fragment key={i}>
          {i > 0 && <span className="mx-1 text-muted-foreground/50">·</span>}
          {b}
        </React.Fragment>
      ))}
    </div>
  );
}

function dotColor(status: string): string {
  const s = (status || "").toUpperCase();
  if (s === "SUCCESS" || s === "COMPLETED") return "bg-emerald-500";
  if (s === "ERROR" || s === "FAILURE") return "bg-red-500";
  if (s === "DENIED") return "bg-orange-500";
  return "bg-primary";
}

export default function GatewayTraceTimeline({ events }: { events: any[] }) {
  if (!events || !events.length) {
    return <div className="text-sm text-muted-foreground text-center py-4">No events</div>;
  }
  return (
    <div className="relative pl-[30px]">
      {/* rail */}
      <div className="absolute left-[11px] top-2 bottom-2 w-0.5 bg-border" />
      <div className="space-y-3">
        {events.map((e, i) => (
          <div key={e.id || i} className="relative">
            {/* status dot on the rail */}
            <span className={cn("absolute left-[-23px] top-[18px] w-2.5 h-2.5 rounded-full ring-2 ring-card z-10", dotColor(e.status))} />
            <div className="rounded-xl border border-border ag-surface px-4 py-3">
              <div className="flex items-center gap-2 flex-wrap mb-1.5">
                {e.eventSequence != null && (
                  <span className="inline-flex items-center justify-center min-w-[22px] h-5 px-1.5 rounded-full text-[10px] font-bold font-mono text-primary bg-primary/10 border border-primary/25">
                    #{e.eventSequence}
                  </span>
                )}
                {e.module && <GatewayModuleBadge module={e.module} />}
                <GatewayStatusBadge status={e.status} />
                <span className="text-[11px] text-muted-foreground/70 font-mono ml-1">{formatTimestamp(e.timestamp)}</span>
              </div>
              <div className="text-[13px] font-semibold text-foreground break-words">{eventTypeLabel(e.eventType)}</div>
              <EventDetail event={e} />
              {e.errorMessage && (
                <div className="text-[12px] text-red-600 dark:text-red-400 mt-1.5 break-words whitespace-pre-wrap">{e.errorMessage}</div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
